import { useState, useCallback } from 'react';
import { env } from 'constants/env';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';

export interface PolicyDocument {
  identifier: string;
  name: string;
  viewUrl: string;
}

interface PolicyDocumentsResponse {
  documents: Array<{ identifier: string; name: string }>;
}

interface UsePolicyDocumentsResult {
  documents: PolicyDocument[];
  isLoading: boolean;
  isSigned: boolean;
  isSigning: boolean;
  error: string | null;
  signError: string | null;
  fetch: () => Promise<void>;
  sign: (checked: boolean) => Promise<void>;
}

export const usePolicyDocuments = (
  appId: string,
  paymentPlanId: string
): UsePolicyDocumentsResult => {
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  // starts true to prevent checkbox interaction before fetch() is called
  const [isLoading, setIsLoading] = useState(true);
  const [isSigned, setIsSigned] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await httpCheckoutAPI.get({
        url: `/bindapi/insurance_applications/${appId}/policy_documents/list`,
      });
      const raw = (response.data as PolicyDocumentsResponse).documents ?? [];
      setDocuments(
        raw.map((doc) => ({
          identifier: doc.identifier,
          name: doc.name,
          viewUrl: `${env.apiHost}/bindapi/insurance_applications/${appId}/policy_documents/${paymentPlanId}/view/${doc.identifier}`,
        }))
      );
    } catch {
      setError(
        'Unable to load policy documents. Please refresh and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [appId, paymentPlanId]);

  const sign = useCallback(
    async (checked: boolean) => {
      setSignError(null);
      setIsSigning(true);
      const previousValue = isSigned;
      setIsSigned(checked);
      try {
        await httpCheckoutAPI.post({
          url: `/bindapi/insurance_applications/${appId}/policy_documents/sign`,
          data: {
            paymentPlanId,
            command: checked ? 'sign' : 'revoke',
          },
        });
      } catch {
        setIsSigned(previousValue);
        setSignError('Unable to update document agreement. Please try again.');
      } finally {
        setIsSigning(false);
      }
    },
    [appId, paymentPlanId, isSigned]
  );

  return {
    documents,
    isLoading,
    isSigned,
    isSigning,
    error,
    signError,
    fetch,
    sign,
  };
};
